import { Component, ElementRef, AfterViewInit, ViewChild, OnDestroy, Output, EventEmitter, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { select, Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import { map, debounceTime, tap, takeUntil, take, filter } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';

import { IAuroraState } from '@apps/aurora.state';
import * as fromRootStore from '@apps/root/store';

import { GlobalSearchContext, GlobalSearchService } from './service';

@Component({
    selector: 'aa-header-search',
    templateUrl: './header-search.component.html',
    styleUrls: [
        './header-search.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderSearchComponent implements AfterViewInit, OnDestroy
{
    @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
    @Output() focused: EventEmitter<boolean> = new EventEmitter<boolean>();
    public value: string = '';
    public searchGroup: UntypedFormGroup;
    public isNonEmpty: Observable<boolean> = of(false);
    public isSearchActivated: Observable<boolean> = of(false);
    public isPanelShown: Observable<boolean> = of(false);
    private destroyator = new Subject();
    private hasFocus: boolean = false;

    constructor (
        private actions: Actions,
        private store: Store<IAuroraState>,
        private formBuilder: UntypedFormBuilder,
        private globalSearchService: GlobalSearchService
    )
    {
        this.store
            .pipe(
                select(fromRootStore.getSearchValue),
                take(1),
                tap((searchValue: string) =>
                    this.searchGroup = this.formBuilder.group({
                        search: [searchValue]
                    })
                )
            )
            .subscribe();
        this.isNonEmpty = this.globalSearchService.contextor
            .pipe(
                map((ctxt: GlobalSearchContext) =>
                    ctxt.searchValue !== ''
                )
            );
        this.isSearchActivated = this.globalSearchService.contextor
            .pipe(
                map((ctxt: GlobalSearchContext) =>
                    ctxt.isSearchActivated
                )
            );
        this.isPanelShown = this.globalSearchService.contextor
            .pipe(
                map((ctxt: GlobalSearchContext) =>
                    ctxt.isPanelShown
                )
            );
        this.globalSearchService.contextor
            .pipe(
                filter((ctxt: GlobalSearchContext) =>
                    ctxt.isFocused
                ),
                tap(() =>
                    this.searchInput.nativeElement.focus()
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();

        this.actions
            .pipe(
                ofType(fromRootStore.RootSearchStoreActions.RootSearchResultsClearRequested),
                tap(() =>
                    this.clearSearchResults()
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngAfterViewInit ()
    {
        this.searchGroup.valueChanges
            .pipe(
                debounceTime(500),
                tap((result: { search: string }) =>
                    this.globalSearchService.launchSearch(result.search)
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    ngOnDestroy (): void
    {
        this.destroyator.next(null);
        this.destroyator.complete();
    }

    @HostListener('document:keyup', ['$event'])
    handleKeyDown (event: KeyboardEvent)
    {
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (event.key === 'Escape')
        {
            this.hideSearchResults();
        }

        if (this.hasFocus && event.key === 'Enter')
        {
            this.globalSearchService.handleEnterKey();
        }

        if (event.shiftKey && (event.ctrlKey || event.metaKey) && (event.key === 'F' || event.key === 'f'))
        {
            this.showSearchResults();
        }
    }

    onFocus = (): void =>
    {
        this.hasFocus = true;
    };

    onBlur= (): void =>
    {
        this.hasFocus = false;
    };

    clearSearchResults (): void
    {
        this.searchGroup.reset();
        this.globalSearchService.clearResults();
    }

    showSearchResults (): void
    {
        this.globalSearchService.showResults();
    }

    hideSearchResults (): void
    {
        this.globalSearchService.hideResults();
    }
}
